import React, { useCallback, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Select, MenuItem, FormControl, TextField, Box, Chip, Autocomplete } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import { RecipeCellProps, Source, RecipeColumnState } from './types';
import { RowStore } from './stores/RowStore';
import MultiSelectPopup from './MultiSelectPopup';
import { useTableKeyboardNavigation } from '@/hooks/useTableKeyboardNavigation';

// Format value based on column type and precision
const formatValue = (value: string | number | string[], column: RecipeColumnState): string => {
  if (value === '' || value === null || value === undefined) return '';

  // Handle arrays (multi-select values) - convert to comma-separated string
  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (column.isDateTime) {
    return String(value || '');
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return String(value);
  const precision = column.precision || 0;

  if (column.isNumber) {
    return numValue.toFixed(precision);
  }

  if (column.isScientific) {
    return numValue.toExponential(precision);
  }

  return String(value);
};

const RecipeCell = observer(
  ({
    row,
    column,
    groupColor: _groupColor,
    groupSymbol: _groupSymbol,
    readonly = false,
    onValueChange,
    onArrowDownAtLastRow,
    isTwoLine: _isTwoLine,
  }: RecipeCellProps) => {
    const symbol = column.symbol || '';
    const isCalculated = useMemo(() => {
      return (
        column.sourceType === Source.Formula ||
        column.sourceType === Source.OverwrittableFormula ||
        column.sourceType === Source.Constant ||
        column.sourceType === Source.Autoincrement
      );
    }, [column.sourceType]);

    /** Formula-backed but user-overwritable (e.g. Mass measured) — editable styling, not read-only gray. */
    const isReadOnlyCalculated = useMemo(() => {
      return (
        column.sourceType === Source.Formula ||
        column.sourceType === Source.Constant ||
        column.sourceType === Source.Autoincrement
      );
    }, [column.sourceType]);

    // Access cacheVersion only for calculated columns to ensure MobX tracks cache invalidation
    // This triggers re-renders when formulas need to recalculate
    // Use void to ensure it's accessed but not interfere with other MobX tracking
    if (row instanceof RowStore && isCalculated) {
      void row.cacheVersion; // Access to track for MobX reactivity
    }
    const currentValue = row instanceof RowStore ? row.getValue(symbol) || '' : row.values.get(symbol) || '';

    const cellBackgroundColor = useMemo(() => {
      if (!isReadOnlyCalculated && !readonly && !column.readonly) {
        return '#fffef0';
      }
      return 'transparent';
    }, [isReadOnlyCalculated, readonly, column.readonly]);

    const { elementRef, handleKeyDown } = useTableKeyboardNavigation<HTMLDivElement>({
      enabled: !readonly && !column.readonly,
      onArrowKey: (direction, event) => {
        if (direction !== 'down' || !onArrowDownAtLastRow) return false;
        const target = event.currentTarget as HTMLElement;
        const td = target.closest('td');
        if (!(td instanceof HTMLTableCellElement)) return false;
        onArrowDownAtLastRow(td.cellIndex);
        return true;
      },
    });
    const getInitialValue = useCallback(() => {
      if (column.isMultiSelect) {
        if (Array.isArray(currentValue)) {
          return currentValue;
        }
        if (typeof currentValue === 'string' && currentValue) {
          // Parse comma-separated string back to array
          return currentValue
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v);
        }
        return [];
      }
      return currentValue;
    }, [currentValue, column.isMultiSelect]);

    // Local state for input value to prevent unnecessary rerenders
    const [localValue, setLocalValue] = useState(getInitialValue());

    // FIXME: State sync in useEffect (ESLint); prefer derive/key — docs/guide/effects-and-async-state.md
    // Update local value when currentValue changes (from external updates)
    React.useEffect(() => {
      setLocalValue(getInitialValue());
    }, [currentValue, column.isMultiSelect, getInitialValue]);

    // Generate tooltip text with formula for formula columns
    const getTooltipText = useCallback(() => {
      // Show formula information for calculated columns
      if (column.sourceType === Source.Formula || column.sourceType === Source.OverwrittableFormula) {
        if (column.source) {
          return `${column.symbol || ''} = ${column.source}`;
        }
      } else if (column.sourceType === Source.FormulaPerRow) {
        // FormulaPerRow support - check if row has formulas (only in RecipeRowState, not RowStore yet)
        if (!(row instanceof RowStore) && 'formulas' in row && Array.isArray(row.formulas)) {
          const rowFormula = row.formulas.find((formula: string[]) => formula[0] === column.symbol);
          if (rowFormula && rowFormula[1]) {
            return `${column.symbol || ''} = ${rowFormula[1]}`;
          }
        }
      }
      return column.symbol || '';
    }, [column.symbol, column.sourceType, column.source, row]);

    // Change handler for typing - only updates local state
    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      setLocalValue(value);
      // No parent notification during typing
    }, []);

    // Immediate change handler for dropdowns
    const handleDropdownChange = useCallback(
      (event: any) => {
        const value = event.target.value;
        setLocalValue(value);
        // Always save dropdown changes immediately for better UX
        if (value !== currentValue) {
          onValueChange(value);
        }
      },
      [currentValue, onValueChange]
    );

    // Blur handler - always save data, but only trigger calculations for formula-relevant cells
    const handleBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value;
        // Format the value for scientific notation and numbers before saving
        const formattedValue = formatValue(value, column);
        // Always save the data if it changed
        if (formattedValue !== currentValue) {
          onValueChange(formattedValue);
        }
      },
      [currentValue, onValueChange, column]
    );

    // Render different input types based on column configuration
    if (column.isNumber || column.isScientific) {
      return (
        <TextField
          ref={elementRef as any}
          fullWidth
          variant="outlined"
          size="small"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown as any}
          disabled={readonly || column.readonly}
          inputProps={{
            style: { textAlign: 'right', backgroundColor: cellBackgroundColor },
            title: getTooltipText(),
          }}
        />
      );
    }

    // HTML type - render HTML content (check before isText to avoid fallthrough)
    if (column.isHTML) {
      if (readonly || column.readonly) {
        return (
          <div
            className="readonly-cell-display"
            title={getTooltipText()}
            style={{
              padding: '8px',
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.875rem',
              backgroundColor: cellBackgroundColor,
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: String(localValue || '') }} />
          </div>
        );
      }
      // In edit mode, HTML fields are still readonly (can't edit HTML directly)
      return (
        <div
          className="readonly-cell-display"
          title={getTooltipText()}
          style={{
            padding: '8px',
            minHeight: '40px',
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.875rem',
            backgroundColor: cellBackgroundColor,
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: String(localValue || '') }} />
        </div>
      );
    }

    if (column.isText || column.isDateTime) {
      return (
        <TextField
          ref={elementRef as any}
          fullWidth
          variant="outlined"
          size="small"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown as any}
          disabled={readonly || column.readonly}
          inputProps={{
            style: { backgroundColor: cellBackgroundColor },
            title: getTooltipText(),
          }}
        />
      );
    }

    // ExtendibleList: Select with predefined values + allow custom input
    if (column.isExtendibleList) {
      const dropdownOptions = (column.dropdownValues || []).map((opt) => (typeof opt === 'string' ? opt : opt.label));

      if (readonly || column.readonly) {
        if (!localValue) {
          return <div className="readonly-select-display">None</div>;
        }
        return <div className="readonly-select-display">{String(localValue)}</div>;
      }

      return (
        <Autocomplete
          ref={elementRef as any}
          freeSolo
          options={dropdownOptions}
          value={String(localValue)}
          onChange={(_, newValue) => {
            const value = newValue || '';
            setLocalValue(value);
            onValueChange(value);
          }}
          onInputChange={(_, newValue) => {
            // Allow typing custom values
            setLocalValue(newValue);
          }}
          onBlur={() => {
            // Commit the value on blur
            onValueChange(localValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder="Select or type..."
              inputProps={{
                ...params.inputProps,
                style: { backgroundColor: cellBackgroundColor },
                title: getTooltipText(),
              }}
            />
          )}
          disabled={readonly || column.readonly}
          fullWidth
          size="small"
          sx={{ backgroundColor: cellBackgroundColor }}
        />
      );
    }

    if (column.isList || column.isOptionalList) {
      // Show "None" for readonly single-select only if no values
      if (readonly || column.readonly) {
        if (!localValue) {
          return <div className="readonly-select-display">None</div>;
        }
        // If there are values, show them as a simple text display
        const option = (column.dropdownValues || []).find(
          (opt) => (typeof opt === 'string' ? opt : opt.id) === localValue
        );
        const displayValue = typeof option === 'string' ? option : option?.label || localValue;
        return <div className="readonly-select-display">{displayValue}</div>;
      }
      return (
        <FormControl fullWidth size="small" variant="outlined" style={{ backgroundColor: cellBackgroundColor }}>
          <Select
            ref={elementRef as any}
            value={localValue}
            onChange={handleDropdownChange}
            onKeyDown={handleKeyDown as any}
            disabled={readonly || column.readonly}
            displayEmpty
            title={getTooltipText()}
            renderValue={(selected) => {
              if (!selected)
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%' }}>
                    <span
                      style={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontStyle: 'normal',
                        fontSize: '0.75rem',
                        marginTop: '4px',
                        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                        fontWeight: 'normal',
                        cursor: 'pointer',
                      }}
                    >
                      Select...
                    </span>
                  </Box>
                );
              const option = (column.dropdownValues || []).find(
                (opt) => (typeof opt === 'string' ? opt : opt.id) === selected
              );
              const displayValue = typeof option === 'string' ? option : option?.label || selected;

              // Show as chip for consistency with multi-select
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%' }}>
                  <Chip
                    label={displayValue}
                    size="small"
                    sx={{
                      maxWidth: '120px',
                      backgroundColor: 'transparent',
                      border: '1px solid #e0e0e0',
                      fontSize: '0.75rem',
                      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                      fontWeight: 'normal',
                      marginTop: '2px',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  />
                </Box>
              );
            }}
          >
            {localValue ? (
              <MenuItem value="" onClick={() => handleDropdownChange({ target: { value: '' } } as any)}>
                <Box>
                  <ClearIcon fontSize="small" />
                  Clear
                </Box>
              </MenuItem>
            ) : null}
            {(column.dropdownValues || []).map((option, index) => (
              <MenuItem key={index} value={typeof option === 'string' ? option : option.id}>
                {typeof option === 'string' ? option : option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (column.isMultiSelect) {
      // Parse string values into arrays for multi-select
      let selectedValues: string[];
      if (Array.isArray(localValue)) {
        selectedValues = localValue as string[];
      } else if (typeof localValue === 'string' && localValue.trim() !== '') {
        // Split comma-separated string values
        selectedValues = localValue
          .split(',')
          .map((val) => val.trim())
          .filter((val) => val !== '');
      } else {
        selectedValues = [];
      }

      // Convert dropdown values to string array for the MultiSelectPopup component
      const options = (column.dropdownValues || []).map((option) =>
        typeof option === 'string' ? option : String(option.id)
      );

      // Show "None" for readonly multi-select only if no values
      if (readonly || column.readonly) {
        if (selectedValues.length === 0) {
          return <div className="readonly-select-display">None</div>;
        }
        // If there are values, show them as a simple text display
        return <div className="readonly-select-display">{selectedValues.join(', ')}</div>;
      }

      return (
        <MultiSelectPopup
          value={selectedValues}
          options={options}
          onChange={(newValue) => onValueChange(newValue)}
          placeholder="Select options..."
          disabled={readonly || column.readonly}
          width="100%"
          title={getTooltipText()}
        />
      );
    }

    if (column.isLookup) {
      // Show "None" for readonly lookup only if no values
      if (readonly || column.readonly) {
        if (!localValue) {
          return <div className="readonly-select-display">None</div>;
        }
        // If there are values, show them as a simple text display
        const option = (column.dropdownValues || []).find(
          (opt) => (typeof opt === 'string' ? opt : opt.id) === localValue
        );
        const displayValue = typeof option === 'string' ? option : option?.label || localValue;
        return <div className="readonly-select-display">{displayValue}</div>;
      }
      return (
        <FormControl fullWidth size="small" variant="outlined" style={{ backgroundColor: cellBackgroundColor }}>
          <Select
            ref={elementRef as any}
            value={localValue}
            onChange={handleDropdownChange}
            onKeyDown={handleKeyDown as any}
            disabled={readonly || column.readonly}
            displayEmpty
            title={getTooltipText()}
            renderValue={(selected) => {
              if (readonly || column.readonly) {
                if (!selected) return 'None';
                const option = (column.dropdownValues || []).find(
                  (opt) => (typeof opt === 'string' ? opt : opt.id) === selected
                );
                return typeof option === 'string' ? option : option?.label || selected;
              }
              if (!selected)
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%' }}>
                    <span
                      style={{
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontStyle: 'normal',
                        fontSize: '0.75rem',
                        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                        fontWeight: 'normal',
                      }}
                    >
                      Select a value
                    </span>
                  </Box>
                );
              const option = (column.dropdownValues || []).find(
                (opt) => (typeof opt === 'string' ? opt : opt.id) === selected
              );
              const displayValue = typeof option === 'string' ? option : option?.label || selected;

              // Show as chip for consistency with multi-select
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%' }}>
                  <Chip
                    label={displayValue}
                    size="small"
                    sx={{
                      maxWidth: '120px',
                      backgroundColor: 'transparent',
                      border: '1px solid #e0e0e0',
                      fontSize: '0.75rem',
                      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                      fontWeight: 'normal',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  />
                </Box>
              );
            }}
          >
            {localValue ? (
              <MenuItem value="" onClick={() => handleDropdownChange({ target: { value: '' } } as any)}>
                <Box>
                  <ClearIcon fontSize="small" />
                  Clear
                </Box>
              </MenuItem>
            ) : null}
            {column.dropdownValues.map((option, index) => (
              <MenuItem key={index} value={typeof option === 'string' ? option : option.id}>
                {typeof option === 'string' ? option : option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    // Fallback to text input
    // In readonly mode, render plain HTML instead of MUI components for better performance
    if (readonly || column.readonly) {
      const displayValue = formatValue(localValue, column);
      return (
        <div
          className="readonly-cell-display"
          title={getTooltipText()}
          style={{
            padding: '8px',
            minHeight: '40px',
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.875rem',
            backgroundColor: cellBackgroundColor,
          }}
        >
          {displayValue || '\u00A0'}
        </div>
      );
    }

    // Format calculated values for display (DateTime values are already formatted as strings)
    const displayValue = isCalculated && !column.isDateTime ? formatValue(localValue, column) : localValue;

    return (
      <TextField
        ref={elementRef}
        fullWidth
        variant="outlined"
        size="small"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown as any}
        disabled={readonly || column.readonly || isReadOnlyCalculated}
        inputProps={{
          style: { backgroundColor: cellBackgroundColor },
          title: getTooltipText(),
        }}
      />
    );
  }
);

RecipeCell.displayName = 'RecipeCell';

// Memoize RecipeCell to prevent unnecessary re-renders when other cells change
// Only re-render if props actually change
export default React.memo(RecipeCell, (prevProps, nextProps) => {
  // Compare all props except onValueChange (which is recreated on each render but functionally equivalent)
  if (prevProps.readonly !== nextProps.readonly) return false;
  if (prevProps.isTwoLine !== nextProps.isTwoLine) return false;
  if (prevProps.groupColor !== nextProps.groupColor) return false;
  if (prevProps.groupSymbol !== nextProps.groupSymbol) return false;
  if (prevProps.column !== nextProps.column) return false;
  if (prevProps.row !== nextProps.row) return false;

  // For MobX RowStore, the observer pattern will handle reactivity
  // For plain RecipeRowState, we need to check if the value for this column changed
  if (!(prevProps.row instanceof RowStore) && !(nextProps.row instanceof RowStore)) {
    // Both are plain state objects - compare the actual value for this column
    const prevValue = prevProps.row.values.get(prevProps.column.symbol);
    const nextValue = nextProps.row.values.get(nextProps.column.symbol);
    if (prevValue !== nextValue) return false;

    // Also check if showComment changed (affects rendering)
    if (prevProps.row.showComment !== nextProps.row.showComment) return false;
  }

  // Props are equal, don't re-render
  return true;
});
